import { useCallback, useMemo } from "react";

import { orderBy } from "lodash";

import { type TabId } from "../tabs";

import { type TabState, useTabState } from "./use-tab-state";

export const tabRanking = {
  breakdown: 1,
  campaign: 2,
  adset: 3,
  ad: 4,
} as { [tabId in TabId]: number };

const tabsByRanking = orderBy(
  Object.entries(tabRanking).map(([tab, ranking]) => ({ tab, ranking })),
  "ranking",
  "desc",
).map((i) => i.tab) as TabId[];

export function useLatestTabAncestor() {
  const adTabState = useTabState("ad");
  const adsetTabState = useTabState("adset");
  const campaignTabState = useTabState("campaign");
  const breakdownTabState = useTabState("breakdown");

  const tabStates = useMemo(() => {
    return {
      breakdown: breakdownTabState,
      campaign: campaignTabState,
      adset: adsetTabState,
      ad: adTabState,
    };
  }, [adTabState, adsetTabState, breakdownTabState, campaignTabState]);

  return useCallback(
    (id: TabId) => {
      let ancestor: TabState | null = null;
      let ancestorId: TabId | null = null;
      for (const tab of tabsByRanking) {
        // if this is a more granular tab or the tab itself, do nothing
        if (tabRanking[tab] >= tabRanking[id]) {
          continue;
        }

        const potentialAncestor = tabStates[tab].tabState;
        if (potentialAncestor?.appliedFiltersState) {
          ancestor = potentialAncestor;
          ancestorId = tab;
          break;
        }
      }
      return { ancestor, ancestorId };
    },
    [tabStates],
  );
}
